import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  CompanyTypeOption,
  IndustryType,
  listIndustries,
  updateIndustry,
} from "@/services/companyClassificationApi";

import {
  ALL,
  companyTypeLabel,
  DEFAULT,
  industryOverridePayload,
  industryOverrideValue,
  NONE,
} from "./classification";

export function IndustriesTab({ types }: { types: CompanyTypeOption[] }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<IndustryType[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(
        await listIndustries(navigate, {
          q: search.trim(),
          type: typeFilter === ALL ? undefined : typeFilter === NONE ? "none" : typeFilter,
          only_overridden: onlyOverridden,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate, search, typeFilter, onlyOverridden]);

  useEffect(() => {
    const timer = setTimeout(refresh, 250);
    return () => clearTimeout(timer);
  }, [refresh]);

  const handleOverride = async (row: IndustryType, value: string) => {
    setSavingId(row.linkedin_id);
    setError(null);
    try {
      await updateIndustry(row.linkedin_id, { ...industryOverridePayload(value), note: row.note }, navigate);
      setNotice(`« ${row.name} » mise à jour — les entreprises concernées sont reclassées sous une minute.`);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="h-9 w-64"
          placeholder="Rechercher une industrie ou un ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les types effectifs</SelectItem>
            <SelectItem value={NONE}>Sans type</SelectItem>
            {types.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch id="only-overridden" checked={onlyOverridden} onCheckedChange={setOnlyOverridden} />
          <Label htmlFor="only-overridden">Seulement les industries modifiées</Label>
        </div>
        <span className="ml-auto text-xs text-muted-foreground">{loading ? "Chargement…" : `${rows.length} industries`}</span>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">ID</TableHead>
            <TableHead>Industrie</TableHead>
            <TableHead>Parent</TableHead>
            <TableHead>Type par défaut</TableHead>
            <TableHead>Type effectif</TableHead>
            <TableHead className="w-72">Correction</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.linkedin_id}>
              <TableCell className="text-xs text-muted-foreground">{row.linkedin_id}</TableCell>
              <TableCell className="font-medium">{row.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{row.parent_name ?? "—"}</TableCell>
              <TableCell className="text-sm">{row.default_type ? companyTypeLabel(row.default_type) : "—"}</TableCell>
              <TableCell>
                {row.effective_type ? (
                  <div className="flex flex-col">
                    <Badge variant={row.inherited_from ? "outline" : "secondary"} className="w-fit">
                      {companyTypeLabel(row.effective_type)}
                    </Badge>
                    {row.inherited_from && (
                      <span className="text-xs text-muted-foreground">hérité de {row.inherited_from_name}</span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Sans type</span>
                )}
              </TableCell>
              <TableCell>
                <Select
                  value={industryOverrideValue(row)}
                  onValueChange={(value) => handleOverride(row, value)}
                  disabled={savingId === row.linkedin_id}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEFAULT}>Par défaut</SelectItem>
                    <SelectItem value={NONE}>Aucun type</SelectItem>
                    {types.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
